import React from "react";
import { makeStyles } from "@mui/styles"; 
import { SxProps, Theme } from "@mui/material/styles";
import { Chip, Box } from "@mui/material";
import { statusToColour, statusToLabel } from "../../util/statusMapping";

export type StatusLabelSize = "extraSmall" | "mini" | "small" | "extraNarrow" | "medium";

interface StatusLabelProps {
	status: string;
	size?: StatusLabelSize;
	sx?: SxProps<Theme>;
}

type StatusLabelStyleProps = Pick<StatusLabelProps, "status" | "size">;

const SIZES = {
	extraSmall: "extraSmall" as const,
	mini: "mini" as const,
	small: "small" as const,
	extraNarrow: "extraNarrow" as const,
	medium: "medium" as const,
};

const useStyles = makeStyles<Theme, StatusLabelStyleProps>((theme) => ({
	chip: (props) => {
		const baseStyles = {
			background: statusToColour(props.status),
			color: theme.palette.common.white,
		};

		switch (props.size) {
			case SIZES.extraSmall:
				return {
					...baseStyles,
					height: 18,
					fontSize: "0.6rem",
					lineHeight: 1,
					fontWeight: 500,
					"& span": {
						paddingLeft: theme.spacing(0.75),
						paddingRight: theme.spacing(0.75),
					},
				};
			case SIZES.mini:
				return {
					...baseStyles,
					height: 22,
					fontSize: "0.72rem",
					lineHeight: 1,
					fontWeight: 500,
					"& span": {
						paddingLeft: theme.spacing(0.75),
						paddingRight: theme.spacing(0.75),
					},
				};
			case SIZES.small:
				return {
					...baseStyles,
					height: 24,
					fontSize: "0.75rem",
					"& span": {
						paddingLeft: theme.spacing(1),
						paddingRight: theme.spacing(1),
					},
				};
			case SIZES.extraNarrow:
				return {
					...baseStyles,
					height: 25,
					fontSize: "0.75rem",
					"& span": {
						paddingLeft: theme.spacing(0.7),
						paddingRight: theme.spacing(0.7),
					},
				};
			case SIZES.medium:
			default:
				return {
					...baseStyles,
					height: 28,
					fontSize: "0.8125rem",
					"& span": {
						paddingLeft: theme.spacing(1.2),
						paddingRight: theme.spacing(1.2),
					},
				};
		}
	},
}));

const StatusLabel: React.FC<StatusLabelProps> = (props) => {
	const { status, size = SIZES.small, sx } = props;
	const classes = useStyles({ status, size });

	const chipSize = size === SIZES.medium ? "medium" : "small";

	return (
		<Box>
			<Chip label={statusToLabel(status)} size={chipSize} className={classes.chip} sx={sx} />
		</Box>
	);
};

export default StatusLabel;
